import { Options } from 'highcharts';
import { ParsedQuery } from 'query-string';
import React, { FC, ReactNode } from 'react';
import { useSearch } from 'hooks/use-search';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { TemplateInsightBaseChart } from './template-insight-base-chart';

export enum ChartZoomLevel {
  Monthly = 'monthly',
  Weekly = 'weekly',
}
export type ZoomChartOption = Record<ChartZoomLevel, Options>;

interface Props {
  chartOptions: ZoomChartOption;
  title: string;
  tooltip?: ReactNode;
  isLoading: boolean;
  renderEmpty: () => ReactNode;
}

const fromTransformer = (
  value: ParsedQuery<string>,
): { zoom: ChartZoomLevel } => {
  const { zoom } = value;

  if (
    Array.isArray(zoom) &&
    Object.values(ChartZoomLevel).includes(zoom[0] as ChartZoomLevel)
  ) {
    return { zoom: zoom[0] as ChartZoomLevel };
  }
  if (
    typeof zoom === 'string' &&
    Object.values(ChartZoomLevel).includes(zoom as ChartZoomLevel)
  ) {
    return { zoom: zoom as ChartZoomLevel };
  }

  return { zoom: ChartZoomLevel.Monthly };
};

export const ChartWithZoom: FC<Props> = ({
  chartOptions,
  title,
  tooltip = null,
  isLoading,
  renderEmpty,
}) => {
  const translate = useTranslate();
  const [search, setSearch] = useSearch<{ zoom: ChartZoomLevel }>({
    from: fromTransformer,
    to: (value) => {
      return { zoom: value.zoom };
    },
  });
  const tabKeys = Object.values(ChartZoomLevel);
  const tabs = tabKeys.map((key) => ({
    label: translate(`report.zoom-level.${key}` as unknown as TranslationKey),
    key,
  }));
  const chartOption = chartOptions[search.zoom];

  return (
    <TemplateInsightBaseChart
      chartOption={chartOption}
      immutable
      title={title}
      tooltip={tooltip}
      tabs={tabs}
      activeTab={search.zoom}
      onTabSwitch={(zoom) => setSearch({ zoom })}
      renderEmpty={renderEmpty}
      isLoading={isLoading}
    />
  );
};
